import { Paper, TablePagination } from "@mui/material";
import {
	NeutralColor,
	PrimaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import { AdditionalInfoType, Severity } from "node-library/dist/constants";
import {
	FrontendCompanySampleAdditionalInfoFields,
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendSample,
	FrontendSampleAdditionalInfoMapping,
	ParsedImportSampleRow,
} from "node-library/dist/portal";
import { ChangeEvent, DragEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonTextLoadingAnimation } from "../../../../common-components/animation/ButtonLoadingAnimation";
import { GreenButton, GreyButton } from "../../../../common-components/button";
import { CustomDrawer } from "../../../../common-components/drawers/drawer";
import { StepsBar } from "../../../../common-components/steps-bar";
import {
	DeleteIcon,
	EditIcon,
	UploadIcon,
} from "../../../../common-components/svgIcons";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { sendPOSTRequest } from "../../../../requestHelper/sendRequest";
import * as XLSX from "xlsx";
import EditSampleModal from "./editSampleModal";
import {
	normalizeHeader,
	parseBoolean,
	valueToString,
} from "../../../../helpers/importHelper";

interface Props {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	organizationId: string;
	selectedTestCategory: FrontendCompanyTestCategory;
	setOrganizationSamples: React.Dispatch<
		React.SetStateAction<FrontendSample[]>
	>;
	reloadOrganizationDetailedInfo: () => void;
}

function rowHasAnyFieldContent(row: ParsedImportSampleRow): boolean {
	return (
		row.sampleName.trim() !== "" ||
		row.lotCode.trim() !== "" ||
		row.matrix.trim() !== "" ||
		row.subMatrix.trim() !== "" ||
		row.sampleDescription.trim() !== ""
	);
}

/** Same sub-matrix label logic as `editSampleModal` sub-matrix dropdown */
function getSubMatrixDisplayTitle(m: FrontendCompanySampleMatrix): string {
	const split = m.subMatrix.split(`${m.matrix} -`);
	return split.length > 1 ? split[1].trim() : m.subMatrix;
}

function findMatchingSampleMatrix(
	row: ParsedImportSampleRow,
	sampleMatrices: FrontendCompanySampleMatrix[],
): FrontendCompanySampleMatrix | undefined {
	if (row.isCustomMatrix) return undefined;
	const matrixKey = row.matrix.trim().toLowerCase();
	const subKey = row.subMatrix.trim().toLowerCase();
	if (!matrixKey || !subKey) return undefined;
	const candidates = sampleMatrices.filter(
		(m) => m.matrix.trim().toLowerCase() === matrixKey,
	);
	for (const m of candidates) {
		const displayTitle = getSubMatrixDisplayTitle(m).trim().toLowerCase();
		const fullSub = m.subMatrix.trim().toLowerCase();
		if (subKey === fullSub || subKey === displayTitle) {
			return m;
		}
	}
	return undefined;
}

function computeRowErrors(
	rows: ParsedImportSampleRow[],
	sampleMatrices: FrontendCompanySampleMatrix[],
	additionalInfoFields: FrontendCompanySampleAdditionalInfoFields[], // Add this
	translation: (key: string) => string,
): Map<number, string> {
	const errors = new Map<number, string>();
	rows.forEach((row, index) => {
		const messages: string[] = [];
		const hasContent = rowHasAnyFieldContent(row);
		if (!hasContent) {
			errors.set(index, translation("ImportRowEmpty"));
			return;
		}
		if (!row.sampleName.trim() || !row.lotCode.trim()) {
			messages.push(translation("ImportRowMissingRequired"));
		}
		if (row.isCustomMatrix) {
			if (!row.matrix.trim()) {
				messages.push(translation("ImportRowCustomMatrixRequired"));
			}
		} else {
			const match = findMatchingSampleMatrix(row, sampleMatrices);
			if (!match) {
				messages.push(translation("ImportRowMatrixMismatch"));
			}
		}

		additionalInfoFields.forEach((field) => {
			if (field.mandatory && !row.additionalInfo[field.fieldId]?.trim()) {
				messages.push(
					`${field.additionalFieldName} ${translation("IsRequired")}`,
				);
			}
		});
		if (messages.length > 0) {
			errors.set(index, messages.join(", "));
		}
	});
	return errors;
}

export default function UploadSampleImportFileDrawer(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		open,
		setOpen,
		organizationId,
		selectedTestCategory,
		reloadOrganizationDetailedInfo,
	} = props;
	const [importStarted, setImportStarted] = useState<boolean>(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [parsedRows, setParsedRows] = useState<ParsedImportSampleRow[]>([]);
	/** Row index in `parsedRows` → validation error message */
	const [errorRows, setErrorRows] = useState<Map<number, string>>(new Map());
	const [dragging, setDragging] = useState<boolean>(false);

	const [editingSample, setEditingSample] = useState<
		FrontendSample | undefined
	>();
	const [editingSampleIndex, setEditingSampleIndex] = useState<number | null>(
		null,
	);

	const [editingSampleModalOpen, setEditingSampleModalOpen] =
		useState<boolean>(false);

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(1);

	const [currentPagination, setCurrentPagination] = useState(0); // MUI TablePagination is 0-indexed
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const ACCEPTED_EXTENSIONS: string[] = [".csv", ".xls", ".xlsx"];
	const REQUIRED_HEADERS: string[] = [
		"Sample Name",
		"Lot Code",
		"Matrix",
		"Sub Matrix",
		"isCustomMatrix",
		"Sample Description",
	];

	const additionalFields: FrontendCompanySampleAdditionalInfoFields[] =
		selectedTestCategory.sampleAdditionalInfo.filter(
			(field) => field.infoType === AdditionalInfoType.SAMPLE,
		) || [];

	useEffect(() => {
		if (open) {
			setSelectedFile(null);
			setErrorRows(new Map());
			setParsedRows([]);
			setImportStarted(false);
			setCurrentPageIndex(1);
			setCurrentPagination(0);
			setRowsPerPage(10);
		}
	}, [open]);

	async function parseAndSetFile(file: File) {
		try {
			setImportStarted(true);
			const extension = file.name.toLowerCase().split(".").pop() || "";
			if (!ACCEPTED_EXTENSIONS.includes(`.${extension}`)) {
				setSelectedFile(null);
				setParsedRows([]);
				setErrorRows(new Map());
				openSnackbar(
					translation("InvalidFile"),
					Severity.ERROR,
					undefined,
					<RedErrorIcon />,
				);
				return;
			}

			const data = await file.arrayBuffer();
			const workbook = XLSX.read(data, { type: "array" });
			const firstSheetName = workbook.SheetNames[0];
			if (!firstSheetName) {
				throw new Error("No worksheet found");
			}
			const sheet = workbook.Sheets[firstSheetName];
			const rawRows: unknown[][] = XLSX.utils.sheet_to_json(sheet, {
				header: 1,
				defval: "",
				raw: false,
				blankrows: false,
			});
			const rowCount = rawRows.length - 1; // Subtract 1 for header
			const ROW_LIMIT = 500;
			if (rowCount == 0) {
				throw new Error("No data rows found");
			}

			if (rowCount > ROW_LIMIT) {
				setSelectedFile(null);
				setParsedRows([]);
				setErrorRows(new Map());
				openSnackbar(
					// You can use a translation key like translation("RowLimitExceeded", { limit: 500 })
					translation("TooManySamplesToImport", {
						rowCount: rowCount,
						rowLimit: ROW_LIMIT,
					}),
					Severity.ERROR,
					undefined,
					<RedErrorIcon />,
				);
				return; // Stop execution
			}

			const headers = rawRows[0].map((header, index) =>
				normalizeHeader(header, index),
			);

			const headerAliasMap: Record<string, keyof ParsedImportSampleRow> = {
				samplename: "sampleName",
				lotcode: "lotCode",
				matrix: "matrix",
				submatrix: "subMatrix",
				iscustommatrix: "isCustomMatrix",
				is_custom_matrix: "isCustomMatrix",
				sampledescription: "sampleDescription",
			};

			const headerIndexMap: Partial<
				Record<keyof ParsedImportSampleRow, number>
			> = {};

			headers.forEach((header, index) => {
				const normalizedHeaderWithoutUnderscore = header.replace(/_/g, "");
				const directMatch = headerAliasMap[header];
				const relaxedMatch = headerAliasMap[normalizedHeaderWithoutUnderscore];
				const matchingCanonical = directMatch || relaxedMatch;
				if (matchingCanonical) {
					headerIndexMap[matchingCanonical] = index;
				}
			});

			//also read the additional infos if exists

			const additionalFieldHeaderMap: Record<number, string> = {}; // Index -> FieldId

			headers.forEach((header, index) => {
				// Normalize header to compare with field names
				const cleanHeader = header.replace(/_/g, "").toLowerCase();

				const matchingField = additionalFields.find(
					(field) =>
						field.additionalFieldName.replace(/\s+/g, "").toLowerCase() ===
						cleanHeader,
				);

				if (matchingField) {
					additionalFieldHeaderMap[index] = matchingField.fieldId;
				}
			});

			const missingHeaderLabels = REQUIRED_HEADERS.filter((headerLabel) => {
				const normalized = normalizeHeader(headerLabel, 0);
				const canonical =
					headerAliasMap[normalized] ||
					headerAliasMap[normalized.replace(/_/g, "")];
				return !canonical || headerIndexMap[canonical] === undefined;
			});
			if (missingHeaderLabels.length > 0) {
				setSelectedFile(null);
				setParsedRows([]);
				setErrorRows(new Map());
				openSnackbar(
					translation(`Missing headers: ${missingHeaderLabels.join(", ")}`),
					Severity.ERROR,
					undefined,
					<RedErrorIcon />,
				);
				return;
			}

			// 2. Map the rows
			const rows: ParsedImportSampleRow[] = rawRows
				.slice(1)
				.map((row: any, index: number) => {
					const isCustomMatrix = parseBoolean(
						valueToString(row[headerIndexMap.isCustomMatrix as number]),
					);

					const parsed: ParsedImportSampleRow = {
						rowNumber: index,
						sampleName: valueToString(row[headerIndexMap.sampleName as number]),
						lotCode: valueToString(row[headerIndexMap.lotCode as number]),
						matrix: valueToString(row[headerIndexMap.matrix as number]),
						subMatrix: valueToString(row[headerIndexMap.subMatrix as number]),
						isCustomMatrix,
						sampleDescription: valueToString(
							row[headerIndexMap.sampleDescription as number],
						),
						additionalInfo: {}, // Initialize
					};

					// Extract values for any dynamic columns found
					Object.keys(additionalFieldHeaderMap).forEach((colIndex) => {
						const fieldId = additionalFieldHeaderMap[Number(colIndex)];
						parsed.additionalInfo[fieldId] = valueToString(
							row[Number(colIndex)],
						);
					});

					if (!isCustomMatrix) {
						const match = findMatchingSampleMatrix(
							parsed,
							selectedTestCategory.sampleMatrices,
						);
						if (match) parsed.matrixId = match.matrixId;
					}
					return parsed;
				});

			const rowErrors = computeRowErrors(
				rows,
				selectedTestCategory.sampleMatrices,
				additionalFields,
				translation,
			);

			setSelectedFile(file);
			setParsedRows(rows);
			setErrorRows(rowErrors);
			setCurrentPageIndex(rowErrors.size > 0 ? 2 : 3);
		} catch (error: any) {
			console.error(error);
			setSelectedFile(null);
			setParsedRows([]);
			setErrorRows(new Map());
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setImportStarted(false);
		}
	}

	const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setDragging(true);
	};

	const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setDragging(false);
	};

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setDragging(false);
		const droppedFiles = event.dataTransfer.files;
		if (droppedFiles && droppedFiles.length > 0) {
			setSelectedFile(droppedFiles[0]);
		}
	};

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = event.target.files;
		if (selectedFiles && selectedFiles.length > 0) {
			setSelectedFile(selectedFiles[0]);
		}
	};

	const handleRemoveFile = () => {
		setSelectedFile(null);
		setParsedRows([]);
		setErrorRows(new Map());
	};

	const headerItems = [
		translation("ImportStepUploadFile"),
		translation("ImportStepResolveIssues"),
		translation("ImportStepReviewInformation"),
	];

	const headerBar: React.ReactNode = (
		<div id="new-sample-header">
			<StepsBar
				items={headerItems}
				currentItem={currentPageIndex - 1}
				setCurrentItem={setCurrentPageIndex}
			></StepsBar>
		</div>
	);

	async function handleImport() {
		setImportStarted(true);
		try {
			const responseData = await sendPOSTRequest(
				"/samples/bulk-import-samples",
				{
					organizationId,
					testCategoryId: selectedTestCategory.testCategoryId,
					rows: parsedRows,
				},
			);

			openSnackbar(
				translation("ImportSuccessful"),
				Severity.SUCCESS,
				translation("ImportCount", { count: parsedRows.length }),
				<GreenTickIcon />,
			);
			reloadOrganizationDetailedInfo();
			setOpen(false);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setImportStarted(false);
		}
	}

	const uploader: React.ReactNode = (
		<div>
			<Paper
				sx={{
					padding: 3,
					border: dragging
						? `2px dashed ${PrimaryColor.PRIMARY_LV5}`
						: `2px dashed ${NeutralColor.NEUTRAL_GREY_300}`,
					textAlign: "center",
					backgroundColor: "#F3F3F5",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					boxShadow: "none", // Add this line to remove the shadow
				}}
				onDragEnter={handleDragEnter}
				onDragOver={(e) => e.preventDefault()}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				<div className="test-report-upload-icon-container">
					<UploadIcon></UploadIcon>
				</div>

				<input
					type="file"
					onChange={handleFileChange}
					style={{ display: "none" }}
					id="file-input"
					accept={ACCEPTED_EXTENSIONS.join(",")}
				/>
				<div style={{ marginTop: "10px" }}>
					{translation("DragDropFileDescription")}
					<label
						htmlFor="file-input"
						style={{ cursor: "pointer", marginTop: 10 }}
					>
						<div>
							<b>
								<u>{translation("chooseFile")}</u>
							</b>
						</div>
					</label>
				</div>
			</Paper>

			<div style={{ marginTop: 3, maxHeight: "500px", overflow: "auto" }}>
				{selectedFile && (
					<div className="upload-report-file-row">
						<div style={{ maxWidth: "80%", wordWrap: "break-word" }}>
							{selectedFile.name}
						</div>
						{!importStarted ? (
							<div
								id="delete-import-file"
								style={{ cursor: "pointer" }}
								onClick={handleRemoveFile}
							>
								<DeleteIcon></DeleteIcon>
							</div>
						) : (
							<GreenTickIcon
								size={20}
								strokeColor="white"
								fillColor={SemanticColor.SUCCESS}
							></GreenTickIcon>
						)}
					</div>
				)}
			</div>
		</div>
	);

	const editSample = (updatedSample: FrontendSample) => {
		if (editingSampleIndex === null) return;

		// 1. Update the specific row in parsedRows
		const newRows = [...parsedRows];
		const updatedAdditionalInfo: Record<string, string> = {};
		updatedSample.sampleAdditionalInfos.forEach((info) => {
			updatedAdditionalInfo[info.fieldId] = String(info.value);
		});

		const updatedRow: ParsedImportSampleRow = {
			...newRows[editingSampleIndex],
			sampleName: updatedSample.sampleName,
			lotCode: updatedSample.lotCode,
			matrix: updatedSample.matrix,
			sampleDescription: updatedSample.sampleDescription,
			additionalInfo: updatedAdditionalInfo,
			matrixId: updatedSample.matrixId,
			isCustomMatrix: updatedSample.isCustomMatrix,
		};
		if (updatedSample.isCustomMatrix) {
			updatedRow.subMatrix = "";
		}
		newRows[editingSampleIndex] = updatedRow;
		setParsedRows(newRows);

		setErrorRows((prevErrors) => {
			const newErrorsMap = new Map(prevErrors);

			newErrorsMap.set(editingSampleIndex, ""); // Clear error if fixed

			return newErrorsMap;
		});

		// 3. Cleanup
		setEditingSample(undefined);
		setEditingSampleIndex(null);
		setEditingSampleModalOpen(false);
	};

	const renderSampleTable = (isErrorPage: boolean) => {
		const rows: ParsedImportSampleRow[] = isErrorPage
			? parsedRows.filter((_, index) => errorRows.has(index))
			: parsedRows;
		const rowsToRender: ParsedImportSampleRow[] = rows.slice(
			currentPagination * rowsPerPage,
			(currentPagination + 1) * rowsPerPage,
		);
		return (
			<div
				style={{
					marginTop: "20px",
					overflowX: "auto",
					border: `1px solid ${NeutralColor.NEUTRAL_GREY_300}`,
					borderRadius: "4px",
				}}
			>
				<table
					style={{
						width: "100%",
						borderCollapse: "collapse",
						textAlign: "left",
						fontSize: "14px",
					}}
				>
					<thead
						style={{
							backgroundColor: "#F9FAFB",
							borderBottom: `1px solid ${NeutralColor.NEUTRAL_GREY_300}`,
						}}
					>
						<tr>
							<th style={{ padding: "12px 16px" }}>{translation("Row")}</th>
							<th style={{ padding: "12px 16px" }}>{translation("LotCode")}</th>
							<th style={{ padding: "12px 16px" }}>{translation("Name")}</th>
							<th style={{ padding: "12px 16px" }}>{translation("Matrix")}</th>
							<th style={{ padding: "12px 16px" }}>
								{translation("SubMatrix")}
							</th>
							<th style={{ padding: "12px 16px" }}>
								{translation("Description")}
							</th>
							{additionalFields.map((field) => (
								<th key={field.fieldId} style={{ padding: "12px 16px" }}>
									{field.additionalFieldName}
								</th>
							))}
							{isErrorPage && (
								<th style={{ padding: "12px 16px" }}>
									{translation("Status")}
								</th>
							)}

							<th style={{ padding: "12px 16px", textAlign: "center" }}>
								{translation("Edit")}
							</th>
						</tr>
					</thead>
					<tbody>
						{rowsToRender.map((row) => {
							const matchingMatrix: FrontendCompanySampleMatrix | undefined =
								props.selectedTestCategory.sampleMatrices.find((matrix) => {
									return matrix.matrixId == row.matrixId;
								});

							const errorString: string | undefined = errorRows.get(
								row.rowNumber,
							);
							return (
								<tr
									key={row.rowNumber}
									style={{
										borderBottom: `1px solid ${NeutralColor.NEUTRAL_GREY_100}`,
									}}
								>
									<td
										style={{
											padding: "12px 16px",
											color: NeutralColor.NEUTRAL_GREY_500,
										}}
									>
										{/* Mocking the Sample ID format from your image */}
										{`${row.rowNumber + 2}`}
									</td>
									<td style={{ padding: "12px 16px" }}>{row.lotCode}</td>
									<td style={{ padding: "12px 16px" }}>{row.sampleName}</td>
									<td style={{ padding: "12px 16px" }}>
										{matchingMatrix ? matchingMatrix.matrix : row.matrix}
									</td>
									<td style={{ padding: "12px 16px" }}>
										{matchingMatrix
											? matchingMatrix.subMatrix.split(" - ")[1] ||
												matchingMatrix.subMatrix
											: row.subMatrix}
									</td>
									<td
										style={{
											padding: "12px 16px",
											color: NeutralColor.NEUTRAL_GREY_600,
										}}
									>
										{row.sampleDescription || "-"}
									</td>
									{additionalFields.map((field) => (
										<td key={field.fieldId} style={{ padding: "12px 16px" }}>
											{row.additionalInfo[field.fieldId] || "-"}
										</td>
									))}
									{isErrorPage && (
										<td
											style={{
												padding: "12px 16px",
												color: errorString
													? SemanticColor.ERROR
													: SemanticColor.SUCCESS,
											}}
										>
											{errorString ? errorString : <GreenTickIcon size={20} />}
										</td>
									)}
									<td style={{ padding: "12px 16px", textAlign: "center" }}>
										<div
											style={{
												cursor: "pointer",
												fontSize: "20px",
												fontWeight: "bold",
											}}
											onClick={() => {
												// Get the definitions from the category to populate the extra fields
												const fieldDefinitions =
													selectedTestCategory.sampleAdditionalInfo.filter(
														(field) =>
															field.infoType === AdditionalInfoType.SAMPLE,
													) || [];

												const sampleAdditionalInfos: FrontendSampleAdditionalInfoMapping[] =
													Object.entries(row.additionalInfo).map(
														([fieldId, fieldValue]) => {
															// Find the full field definition to get infoType, fieldType, etc.
															const definition: FrontendCompanySampleAdditionalInfoFields =
																fieldDefinitions.find(
																	(f) => f.fieldId === fieldId,
																) as FrontendCompanySampleAdditionalInfoFields;

															return {
																fieldId: fieldId,
																value: fieldValue,
																// Spread the definition or map specific fields required by the interface
																infoType: definition.infoType,
																additionalFieldName:
																	definition.additionalFieldName,
																fieldType: definition.fieldType,
																// If your mapping interface eventually needs mandatory/disabled, include them here:
																// mandatory: definition?.mandatory || false,
																// disabled: definition?.disabled || false,
															};
														},
													);

												const sampleToEdit: FrontendSample = {
													sampleId: "",
													matrixId: row.matrixId,
													testCategoryId:
														props.selectedTestCategory.testCategoryId,
													sampleName: row.sampleName,
													lotCode: row.lotCode,
													matrix: row.matrix,
													isCustomMatrix: row.isCustomMatrix,
													isNewSample: true,
													sampleDescription: row.sampleDescription,
													sampleAdditionalInfos: sampleAdditionalInfos,
												};

												setEditingSampleIndex(row.rowNumber); // Assuming you added the tracking state
												setEditingSample(sampleToEdit);
												setEditingSampleModalOpen(true);
											}}
										>
											<EditIcon></EditIcon>
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
				{parsedRows.length > 0 && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							marginTop: "16px",
						}}
					>
						<TablePagination
							component="div"
							count={isErrorPage ? errorRows.size : parsedRows.length}
							page={currentPagination}
							onPageChange={(event: any, newPage: number) => {
								setCurrentPagination(newPage);
							}}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={(event: any) => {
								setRowsPerPage(parseInt(event.target.value, 10));
								setCurrentPagination(0); // Reset to first page
							}}
							rowsPerPageOptions={[5, 10, 25, 50, 100]}
							sx={{
								backgroundColor: "white",
								borderRadius: "8px",
								padding: "6px 16px",
								"& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
									{
										marginTop: "auto",
										marginBottom: "auto",
									},
							}}
						/>
					</div>
				)}
			</div>
		);
	};

	let currentPageConent: React.ReactNode;
	switch (currentPageIndex) {
		case 1:
			currentPageConent = (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						margin: "0px 20px",
					}}
				>
					{uploader}
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							marginTop: "12px",
						}}
					>
						<GreenButton
							key="import-sample-button"
							id="import-sample-submit-button"
							size="flex"
							children={
								importStarted ? (
									<ButtonTextLoadingAnimation
										text={translation("Importing")}
									></ButtonTextLoadingAnimation>
								) : (
									translation("Import")
								)
							}
							disabled={importStarted || !selectedFile}
							onClick={() => {
								if (selectedFile) {
									parseAndSetFile(selectedFile);
								}
							}}
						></GreenButton>
					</div>
				</div>
			);
			break;
		case 2:
			currentPageConent = (
				<div style={{ padding: "0 20px" }}>
					<div
						className="typography-heading-03"
						style={{ marginBottom: "16px" }}
					>
						{translation("ResolveSomeSampleIssues", {
							errorCount: errorRows.size,
							totalCount: parsedRows.length,
						})}
					</div>
					{renderSampleTable(true)}
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							marginTop: "20px",
						}}
					>
						<GreyButton
							onClick={() => setCurrentPageIndex(1)}
							id={"import-back-button"}
						>
							{translation("Back")}
						</GreyButton>
						<GreenButton
							onClick={() => {
								setCurrentPageIndex(3);
								setCurrentPagination(0);
								setRowsPerPage(10);
							}}
							disabled={Array.from(errorRows.values()).some((error) => {
								return error && error.length > 0;
							})}
							id={"resolve-import-issues-button"}
						>
							{translation("Next")}
						</GreenButton>
					</div>
				</div>
			);
			break;
		case 3:
			currentPageConent = (
				<div style={{ padding: "0 20px" }}>
					<div
						className="typography-heading-03"
						style={{ marginBottom: "16px" }}
					>
						{translation("SomeSamplesWillBeImported", {
							count: parsedRows.length,
						})}
					</div>
					{/* Only show valid rows for final review */}
					{renderSampleTable(false)}

					<div
						style={{
							display: "flex",
							justifyContent: "center",
							marginTop: "24px",
							gap: "12px",
						}}
					>
						<GreyButton
							onClick={() => {
								setCurrentPageIndex(2);
								setCurrentPagination(0);
								setRowsPerPage(10);
							}}
							id={"import-back-button"}
						>
							{translation("Back")}
						</GreyButton>
						<GreenButton
							onClick={handleImport}
							disabled={importStarted}
							id={"start-import-button"}
						>
							{importStarted ? (
								<ButtonTextLoadingAnimation text={translation("Importing")} />
							) : (
								translation("ConfirmImport")
							)}
						</GreenButton>
					</div>
				</div>
			);
			break;
	}

	const importSampleDrawer: React.ReactNode = (
		<CustomDrawer
			anchor="right"
			open={open}
			setOpen={setOpen}
			headerText={`${translation("ImportSamples")} - ${props.selectedTestCategory.testCategoryName}`}
			headerDescription={translation("ImportSamplesDescription")}
			drawerWidth={`100%`}
		>
			<div style={{ display: "flex", justifyContent: "center" }}>
				<div style={{ width: "50%" }}>{headerBar}</div>
			</div>
			{currentPageConent}
		</CustomDrawer>
	);
	return (
		<>
			{importSampleDrawer}
			<EditSampleModal
				selectedTestCategory={props.selectedTestCategory}
				editSample={editSample}
				open={editingSampleModalOpen}
				setOpen={setEditingSampleModalOpen}
				sample={editingSample as FrontendSample}
				loading={false}
				setLoading={() => {}}
			></EditSampleModal>
		</>
	);
}
